// Die Übersicht eines Mandanten: Instanz (letzte Antwort), was in den
// Einstellungen fehlt, der Kunde.

import type { ReactNode } from "react";
import { fmtDate, fmtInt, fmtUsd, monthCostUsd } from "../../lib/format";
import { WIZARD_STEPS, checkText } from "../../lib/labels";
import { Link } from "../../router/router";
import { Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { Info } from "../../ui/field";
import { TAB_OF_STEP, type TabProps } from "./tabs";

function Stat({ label, value, code = false }: { label: string; value: ReactNode; code?: boolean }) {
  return (
    <div className="pf-stat">
      <span className="pf-stat__label">{label}</span>
      <span className={`pf-stat__value${code ? " pf-stat__value--code" : ""}`}>{value}</span>
    </div>
  );
}

export function OverviewTab({ tenant: t, checks }: TabProps) {
  const s = t.last_status ?? {};
  const cost = t.last_cost ?? {};
  const l = t.license;
  // Je Schritt eine Zeile mit Link in den passenden Reiter — vorher stand alles
  // in einem Absatz, getrennt durch Punkte, und man musste den Reiter suchen.
  const problems = Object.entries(checks)
    .map(([n, c]) => [Number(n), Object.entries(c.errors)] as const)
    .filter(([, errs]) => errs.length);
  const pending = s.users?.pending;
  return (
    <>
      <Card title="Instanz" hint={t.last_status_at ? `Stand ${fmtDate(t.last_status_at)} — „Aktualisieren“ in der Liste fragt neu` : "Noch nicht abgefragt"}>
        <div className="pf-stats">
          <Stat label="Version" value={s.version ?? "—"} />
          <Stat label="Benutzer" value={`${fmtInt(s.users?.total)}${l.max_users ? ` / ${l.max_users}` : ""}`} />
          <Stat label="aktiv (7 Tage)" value={fmtInt(s.active_users_7d)} />
          <Stat label="Arbeitsbereiche" value={`${fmtInt(s.workspaces)}${l.max_workspaces ? ` / ${l.max_workspaces}` : ""}`} />
          <Stat label={`Kosten ${cost.month ?? ""}`} value={t.last_cost ? fmtUsd(monthCostUsd(cost)) : "—"} />
          <Stat label="Build" value={t.build ?? "—"} code />
        </div>
        {pending ? <Note>{pending === 1 ? "1 Konto wartet auf Freischaltung." : `${fmtInt(pending)} Konten warten auf Freischaltung.`}</Note> : null}
      </Card>
      {problems.length > 0 && (
        <Card title="Unvollständige Einstellungen" hint="Solange etwas fehlt, lässt sich nichts übernehmen." flush>
          <ul className="pf-problems">
            {problems.map(([n, errs]) => (
              <li key={n}>
                <Link className="pf-problems__step" to={`/tenants/${t.id}/${TAB_OF_STEP[n] ?? ""}`}>{WIZARD_STEPS[n - 1]}</Link>
                <span className="pf-problems__list">{errs.map(([k, code]) => <span key={k}><code>{k}</code> {checkText(code)}</span>)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title="Kunde" foot={<Link className="pf-btn pf-btn--sm" to={`/tenants/${t.id}/customer`}>Bearbeiten</Link>}>
        <div className="pf-grid">
          <Info label="Name">{t.name}</Info>
          <Info label="Ansprechpartner">{t.contact_email ? <>{t.contact_name || t.contact_email}<span className="pf-sub">{t.contact_email}</span></> : (t.contact_name || "—")}</Info>
          <Info label="Angelegt">{fmtDate(t.created_at)}<span className="pf-sub">von {t.created_by}</span></Info>
          {t.appwrite.team_id
            ? <>
              <Info label="Appwrite-Team"><span className="mono">{t.appwrite.team_id}</span></Info>
              <Info label="Projekt-Präfix"><span className="mono">{t.appwrite.prefix}</span></Info>
            </>
            : <Info label="Appwrite">—</Info>}
        </div>
      </Card>
    </>
  );
}
